import * as Icons from "lucide-react";
import logger from "../utils/logger";

type IconName = keyof typeof Icons;

/**
 * Map Material Icons (from backend) to Lucide React Icons
 * Backend sends Material Icon names (e.g., "analytics", "question_answer")
 * We need to map them to Lucide icon names (PascalCase)
 */
const materialToLucideIconMap: Record<string, IconName> = {
    // Analytics & Charts
    'analytics': 'LineChart',
    'leaderboard': 'TrendingUp',
    'assessment': 'BarChart3',
    'chart-bar': 'BarChart3',
    'chart-line': 'LineChart',
    'funnel': 'Filter',
    'file-chart': 'FileBarChart',
    'pie_chart': 'PieChart',
    'bar_chart': 'BarChart',
    'show_chart': 'TrendingUp',
    'timeline': 'GitBranch',
    'dashboard': 'LayoutDashboard',
    'insights': 'Lightbulb',

    // Communication
    'question_answer': 'MessageSquare',
    'question-circle': 'HelpCircle',
    'email': 'Mail',
    'campaign': 'Megaphone',
    'megaphone': 'Megaphone',
    'support_agent': 'Headphones',
    'headset': 'Headphones',
    'message-circle': 'MessageCircle',
    'chat': 'MessageCircle',
    'forum': 'MessagesSquare',
    'comment': 'MessageSquare',
    'mail': 'Mail',
    'mail_outline': 'Mail',
    'send': 'Send',
    'call': 'Phone',
    'phone': 'Phone',
    'notifications': 'Bell',
    'notification_important': 'BellRing',
    'announcement': 'Megaphone',

    // Location & Geography
    'location_on': 'MapPin',
    'map-pin': 'MapPin',
    'route': 'Route',
    'place': 'MapPin',
    'map': 'Map',
    'network': 'Network',
    'sitemap': 'GitBranch',
    'my_location': 'Navigation',
    'navigation': 'Navigation',
    'explore': 'Compass',
    'directions': 'Route',
    'near_me': 'Navigation2',

    // People & Groups
    'group': 'Users',
    'people': 'Users',
    'users': 'Users',
    'user-group': 'Users',
    'person': 'User',
    'admin_panel_settings': 'UserCog',
    'user-cog': 'UserCog',
    'user-tie': 'UserCheck',
    'user-star': 'UserPlus',
    'user-check': 'UserCheck',
    'user-shield': 'ShieldCheck',
    'verified_user': 'Shield',
    'shield': 'Shield',
    'badge': 'Award',
    'account_circle': 'UserCircle',
    'person_add': 'UserPlus',
    'group_add': 'UserPlus',
    'supervisor_account': 'Users',
    'contact_page': 'Contact',

    // Shopping & Orders
    'shopping_cart': 'ShoppingCart',
    'shopping-cart': 'ShoppingCart',
    'shopping_bag': 'ShoppingBag',
    'shopping_basket': 'ShoppingBasket',
    'request_quote': 'FileText',
    'cart-plus': 'ShoppingCart',
    'cart-arrow-down': 'ShoppingCart',
    'boxes': 'Package',
    'add_shopping_cart': 'ShoppingCart',
    'remove_shopping_cart': 'ShoppingCart',
    'store': 'Store',
    'storefront': 'Store',
    'point_of_sale': 'CreditCard',

    // Financial
    'account_balance': 'Building2',
    'attach_money': 'DollarSign',
    'discount': 'Percent',
    'percent': 'Percent',
    'receipt': 'Receipt',
    'wallet': 'Wallet',
    'file-invoice': 'FileText',
    'tag': 'Tag',
    'payments': 'CreditCard',
    'payment': 'CreditCard',
    'money': 'DollarSign',
    'currency_rupee': 'IndianRupee',
    'price_check': 'DollarSign',
    'sell': 'Tag',
    'local_offer': 'Tag',
    'loyalty': 'Ticket',
    'savings': 'PiggyBank',
    'account_balance_wallet': 'Wallet',

    // Time & Schedule
    'access_time': 'Clock',
    'clock': 'Clock',
    'event': 'Calendar',
    'event_busy': 'CalendarOff',
    'schedule': 'CalendarDays',
    'calendar': 'Calendar',
    'calendar-check': 'CalendarCheck',
    'calendar-event': 'CalendarDays',
    'calendar-star': 'CalendarDays',
    'today': 'CalendarDays',
    'date_range': 'CalendarRange',
    'timer': 'Timer',
    'alarm': 'AlarmClock',
    'history': 'History',
    'update': 'RefreshCw',
    'schedule_send': 'Send',

    // Activities
    'local_activity': 'Activity',
    'activity': 'Activity',
    'follow_the_signs': 'GitBranch',
    'task': 'CheckSquare',
    'tasks': 'ListTodo',
    'list': 'List',
    'lists': 'List',
    'phone-call': 'PhoneCall',
    'task_alt': 'CheckCircle',
    'check_circle': 'CheckCircle',
    'check-circle': 'CheckCircle',
    'ruler': 'Ruler',
    'check': 'Check',
    'done': 'Check',
    'done_all': 'CheckCheck',
    'assignment': 'ClipboardList',
    'assignment_turned_in': 'ClipboardCheck',
    'fact_check': 'ClipboardCheck',

    // Products & Inventory
    'inventory': 'Package',
    'box': 'Package',
    'box-open': 'PackageOpen',
    'card_giftcard': 'Gift',
    'gift': 'Gift',
    'inventory_2': 'Package2',
    'warehouse': 'Warehouse',
    'category': 'Folder',
    'style': 'Tag',
    'label': 'Tag',
    'qr_code': 'QrCode',
    'barcode': 'Barcode',

    // Settings & Config
    'settings': 'Settings',
    'cog': 'Settings',
    'lock': 'Lock',
    'key': 'Key',
    'keyboard': 'Keyboard',
    'tune': 'SlidersHorizontal',
    'build': 'Wrench',
    'construction': 'HardHat',
    'security': 'Lock',
    'vpn_key': 'Key',
    'password': 'KeyRound',
    'manage_accounts': 'UserCog',

    // Buildings & Sites
    'building': 'Building2',
    'home': 'Home',
    'business': 'Building2',
    'domain': 'Building',
    'apartment': 'Building2',
    'location_city': 'Building2',
    'factory': 'Factory',
    'corporate_fare': 'Building2',

    // Documents & Reports
    'clipboard-list': 'ClipboardList',
    'book-open': 'BookOpen',
    'FilePlay': 'FileVideo',
    'fileplay': 'FileVideo',
    'file-play': 'FileVideo',
    'description': 'FileText',
    'article': 'FileText',
    'file': 'File',
    'folder': 'Folder',
    'folder_open': 'FolderOpen',
    'insert_drive_file': 'File',
    'text_snippet': 'FileText',
    'note': 'StickyNote',
    'note_add': 'FilePlus',
    'summarize': 'FileText',
    'report': 'FileBarChart',
    'print': 'Printer',
    'download': 'Download',
    'upload': 'Upload',
    'cloud_upload': 'Cloud',
    'cloud_download': 'Cloud',

    // Targets & Goals
    'bullseye': 'Target',
    'track_changes': 'Target',
    'ads_click': 'Target',
    'sports_score': 'Trophy',
    'emoji_events': 'Trophy',
    'military_tech': 'Medal',
    'grade': 'Star',
    'new_releases': 'Sparkles',

    // Actions
    'add': 'Plus',
    'add_circle': 'PlusCircle',
    'remove': 'Minus',
    'remove_circle': 'MinusCircle',
    'edit': 'Edit',
    'delete': 'Trash2',
    'delete_forever': 'Trash',
    'save': 'Save',
    'close': 'X',
    'cancel': 'X',
    'clear': 'X',
    'refresh': 'RefreshCw',
    'sync': 'RefreshCw',
    'undo': 'Undo',
    'redo': 'Redo',
    'more_vert': 'MoreVertical',
    'more_horiz': 'MoreHorizontal',
    'menu': 'Menu',
    'search': 'Search',
    'filter_list': 'Filter',
    'sort': 'ArrowUpDown',
    'visibility': 'Eye',
    'visibility_off': 'EyeOff',

    // Navigation
    'arrow_back': 'ArrowLeft',
    'arrow_forward': 'ArrowRight',
    'arrow_upward': 'ArrowUp',
    'arrow_downward': 'ArrowDown',
    'expand_more': 'ChevronDown',
    'expand_less': 'ChevronUp',
    'chevron_left': 'ChevronLeft',
    'chevron_right': 'ChevronRight',
    'first_page': 'ChevronsLeft',
    'last_page': 'ChevronsRight',
    'open_in_new': 'ExternalLink',
    'launch': 'ExternalLink',

    // Status & Indicators
    'error': 'AlertCircle',
    'warning': 'AlertTriangle',
    'info': 'Info',
    'help': 'HelpCircle',
    'check_circle_outline': 'CheckCircle',
    'error_outline': 'XCircle',
    'priority_high': 'AlertCircle',
    'new_label': 'Sparkles',
    'trending_up': 'TrendingUp',
    'trending_down': 'TrendingDown',

    // Misc
    'star': 'Star',
    'star_border': 'Star',
    'outlined_flag': 'Flag',
    'flag': 'Flag',
    'poll': 'BarChart2',
    'thumb_up': 'ThumbsUp',
    'thumb_down': 'ThumbsDown',
    'favorite': 'Heart',
    'bookmark': 'Bookmark',
    'share': 'Share2',
    'link': 'Link',
    'code': 'Code',
    'bug_report': 'Bug',
    'extension': 'Puzzle',
    'power_settings_new': 'Power',
    'lightbulb': 'Lightbulb',
    'bolt': 'Zap',
    'flash_on': 'Zap',
    'wb_sunny': 'Sun',
    'nights_stay': 'Moon',
};

/**
 * REMOVED: Hardcoded route mapping
 * Routes are now managed in the database (rbac_modules table)
 * Columns: web_route (for web app), app_route (for mobile app)
 * This ensures single source of truth and easier route management
 */

// Helper function to get an icon component by its string name
const getIcon = (name: string): React.ReactElement => {
    if (!name) {
        logger.warn('⚠️ Icon name is empty or undefined');
        return <Icons.HelpCircle />;
    }

    // First check if it's a Material Icon that needs mapping
    const lowerName = name.toLowerCase();
    const lucideIconName = materialToLucideIconMap[lowerName] || name;

    const IconComponent = Icons[lucideIconName as IconName] as React.ElementType;

    // Check if the retrieved export is a valid React component.
    // Lucide icons are functions (or objects for forwardRef).
    // This prevents rendering non-component exports like 'createLucideIcon'.
    if (typeof IconComponent === 'function' || (typeof IconComponent === 'object' && IconComponent !== null)) {
        return <IconComponent />;
    }

    // Fallback to a default icon with detailed logging
    logger.warn(`⚠️ ICON NOT MAPPED: "${name}" - Please add to materialToLucideIconMap in LayoutMenuData.tsx`);
    logger.debug(`Icon not found: ${name}, using fallback HelpCircle icon`);
    return <Icons.HelpCircle />;
};

/**
 * Decode HTML entities in routes (fix for backend encoding issue)
 * Converts &#x2F; -> / and &amp; -> &
 */
const decodeHTMLEntities = (str: string): string => {
  if (!str) return str;

  // Create a temporary element to decode HTML entities
  const textarea = document.createElement('textarea');
  textarea.innerHTML = str;
  return textarea.value;
};

/**
 * Modules the backend may return as active but that we deliberately hide from
 * the web admin sidebar. The DB flag still controls mobile visibility (mobile
 * gates tiles by checking is_active on these modules — keeping IRP_ANALYTICS
 * active in the DB is what lets the mobile Tier Level tile render on current
 * APK builds). This frontend whitelist decouples the two surfaces: mobile sees
 * the module as available; web admin pretends it doesn't exist until the IRP
 * frontend wiring is finished.
 *
 * Add a module_code here when its UI isn't shippable yet but mobile/backend
 * legitimately need the record. Remove the entry when you're ready to show it.
 */
const HIDDEN_FROM_WEB_SIDEBAR = ['IRP_ANALYTICS'];

/**
 * Same intent as HIDDEN_FROM_WEB_SIDEBAR but matches on the route path. Needed
 * because `transformBackendToFrontend` runs twice — once on raw backend data
 * (which has `module_code`) and once on already-transformed data (which has
 * `link` but no longer has `module_code`). Path-based check fires on both.
 */
const HIDDEN_FROM_WEB_SIDEBAR_PATHS = ['/analytics/influencer-reward-program'];

/**
 * Transform backend authorization structure to frontend menu structure
 * Backend: { module_id, module_name, route, permissions: {canView, canAdd...}, sub_modules }
 * Frontend: { id, label, link, rights: {view, add...}, subItems }
 */
export const transformBackendToFrontend = (items: any[]): any[] => {
  if (!items || !Array.isArray(items)) {
    return [];
  }

  return items.map(item => {
    // Web-only hide — see HIDDEN_FROM_WEB_SIDEBAR comment above.
    // Match by either module_code (first transform pass, raw backend data) or
    // link/route (second pass on already-transformed data where module_code has
    // been dropped). Belt-and-braces against the transform-runs-twice flow.
    const code = item.module_code || item.moduleCode;
    if (code && HIDDEN_FROM_WEB_SIDEBAR.includes(code)) {
      return null;
    }
    const candidateLink = item.link || item.route || item.web_route || item.webRoute;
    if (candidateLink && HIDDEN_FROM_WEB_SIDEBAR_PATHS.includes(candidateLink)) {
      return null;
    }

    // Decode HTML entities in route (fix for backend encoding issue)
    const rawRoute = item.route || item.link || item.web_route || item.webRoute;
    const route = decodeHTMLEntities(rawRoute);

    // Transform backend structure to frontend structure
    const transformed: any = {
      id: item.module_type || item.module_id || item.id,
      label: item.module_name || item.label,
      link: route, // Use route from backend (now coming from web_route column)
      icon: item.icon,
      // Department codes for filtering (e.g., ['SFA', 'LOYALTY'])
      departments: item.departments || [],
    };

    // Transform permissions to rights
    if (item.permissions) {
      transformed.rights = {
        view: item.permissions.canView || false,
        add: item.permissions.canAdd || false,
        edit: item.permissions.canEdit || false,
        delete: item.permissions.canDelete || false,
        export: item.permissions.canExport || false,
        import: item.permissions.canImport || false,
      };
    } else if (item.rights) {
      // Already in frontend format
      transformed.rights = item.rights;
    }

    // Transform sub_modules to subItems
    const subModules = item.sub_modules || item.subItems;
    if (Array.isArray(subModules) && subModules.length > 0) {
      transformed.subItems = transformBackendToFrontend(subModules);
    }

    return transformed;
  }).filter(Boolean).filter(item => !!item.link);
};

// Helper function to process icons recursively (without re-transforming)
const processIcons = (items: any[]): any[] => {
  return items.map(item => {
    const newItem = { ...item };

    // If the item has an icon string, replace it with the component
    if (item.icon && typeof item.icon === "string") {
      newItem.icon = getIcon(item.icon as IconName);
    }

    // If the item has valid subItems (array with length > 0), process icons recursively
    if (Array.isArray(item.subItems) && item.subItems.length > 0) {
      newItem.subItems = processIcons(item.subItems);
    } else {
      delete newItem.subItems; // ensure no empty subItems property remains
    }

    return newItem;
  });
};

// Recursive function to process menu items and their sub-items
export const processMenuData = (items: any[]): any[] => {
  if (!items || !Array.isArray(items)) {
    return [];
  }

  const transformedItems = transformBackendToFrontend(items);
  return processIcons(transformedItems);
};


/**
 * Super Admin Sidebar Menu Items
 * These are static menu items for the admin module
 */
export const adminSidebarItems = [
  {
    id: 'admin-dashboard',
    label: 'Dashboard',
    link: '/admin/dashboard',
    icon: <Icons.LayoutDashboard />,
  },
  {
    id: 'admin-analytics',
    label: 'Analytics',
    link: '/admin/analytics',
    icon: <Icons.BarChart3 />,
  },
  {
    id: 'admin-clients',
    label: 'Client Management',
    link: '/admin/clients',
    icon: <Icons.Building2 />,
  },
  {
    id: 'admin-trials',
    label: 'Trial Management',
    link: '/admin/trials',
    icon: <Icons.Clock />,
  },
  {
    id: 'admin-modules',
    label: 'Module Management',
    link: '/admin/modules',
    icon: <Icons.Layers />,
  },
  {
    id: 'admin-roles',
    label: 'Role Management',
    link: '/admin/roles',
    icon: <Icons.Shield />,
  },
  {
    id: 'admin-scheduler',
    label: 'Scheduler',
    link: '/admin/scheduler',
    icon: <Icons.Calendar />,
  },
  {
    id: 'admin-communications',
    label: 'Communications',
    link: '/admin/communications',
    icon: <Icons.MessageSquare />,
  },
  {
    id: 'admin-settings',
    label: 'Settings',
    link: '/admin/settings',
    icon: <Icons.Settings />,
  },
];